import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Paper,
  Stack,
  alpha,
  useTheme,
  Collapse,
  Badge,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Person,
  Description,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Download,
  Email,
  Phone,
  Business,
  CalendarToday,
  Refresh,
  Close,
  Visibility,
  TrendingUp,
  PeopleAlt,
  FolderOpen,
  Assignment,
  ManageAccounts as ManageAccountsIcon,
  DeleteOutline,
  MergeType,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const USERS_PAGE_SIZE = 100;

const AdminDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('docs_then_recent');
  const [expandedUser, setExpandedUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [exportingUser, setExportingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(null); // holds the source user id
  const [sendingReminder, setSendingReminder] = useState(null); // holds the user id being emailed
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [dashboardRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, config),
        axios.get(`${API_URL}/api/admin/users?page=1&limit=${USERS_PAGE_SIZE}`, config)
      ]);

      if (dashboardRes.data.success) {
        setDashboardData(dashboardRes.data.data);
      }

      if (usersRes.data.success) {
        setUsers(usersRes.data.data);
        setUsersTotal(usersRes.data.total);
        setUsersPage(1);
      }

      if (isRefresh) toast.success('Data refreshed!');
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMoreUsers = async () => {
    try {
      setLoadingMoreUsers(true);
      const nextPage = usersPage + 1;
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const res = await axios.get(
        `${API_URL}/api/admin/users?page=${nextPage}&limit=${USERS_PAGE_SIZE}`,
        config
      );

      if (res.data.success) {
        setUsers((prev) => [...prev, ...res.data.data]);
        setUsersTotal(res.data.total);
        setUsersPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more users:', error);
      toast.error('Failed to load more users');
    } finally {
      setLoadingMoreUsers(false);
    }
  };

  const handleDownloadDocument = async (userId, docId, fileName) => {
    try {
      setDownloadingDoc(docId);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/admin/users/${userId}/documents/${docId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Check if the response is JSON (contains a URL) or binary (file data)
      const contentType = response.headers['content-type'] || '';
      
      if (contentType.includes('application/json')) {
        // Response is JSON with a presigned URL — parse it
        const text = await response.data.text();
        const json = JSON.parse(text);
        if (json.url) {
          window.open(json.url, '_blank');
        }
      } else {
        // Response is the actual file — download as blob
        const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleExportAllDocuments = async (userId, userName) => {
    try {
      setExportingUser(userId);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/admin/users/${userId}/documents/export-all`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link from blob
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${userName.replace(/\s+/g, '_')}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`All documents for ${userName} exported!`);
    } catch (error) {
      console.error('Export error:', error);
      if (error.response?.status === 404) {
        toast.error('No documents found for this user');
      } else {
        toast.error('Failed to export documents');
      }
    } finally {
      setExportingUser(null);
    }
  };

  const openUserDetails = async (user) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUser(userId);
      const token = localStorage.getItem('token');
      const res = await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) => prev.filter((u) => u._id !== userId));
      setDeleteConfirm(null);
      setExpandedUser(null);
      toast.success(res.data.message || 'User deleted');
      // Refresh dashboard stats
      fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUser(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) => prev.map((u) => u._id === userId ? { ...u, role: newRole } : u));
      toast.success(`Role updated to '${newRole}'`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleSendReminder = async (user) => {
    if (!window.confirm(`Send an application reminder to ${user.email}?`)) return;
    try {
      setSendingReminder(user._id);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/users/${user._id}/application-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Reminder sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleMergeUsers = async () => {
    if (!mergeOpen || !mergeTarget) return;
    try {
      setMerging(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/users/merge`,
        { targetUserId: mergeTarget, sourceUserId: mergeOpen },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Users merged successfully');
      setMergeOpen(null);
      setMergeTarget('');
      setExpandedUser(null);
      fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to merge users');
    } finally {
      setMerging(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.businessName?.toLowerCase().includes(search) ||
      (user.alternateEmails || []).some(e => e.toLowerCase().includes(search))
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aCreatedAt = new Date(a.createdAt).getTime() || 0;
    const bCreatedAt = new Date(b.createdAt).getTime() || 0;

    if (sortMode === 'joined_newest') {
      return bCreatedAt - aCreatedAt;
    }

    if (sortMode === 'joined_oldest') {
      return aCreatedAt - bCreatedAt;
    }

    // Default sort: users with more documents first, then newest joined.
    if (b.documentCount !== a.documentCount) {
      return b.documentCount - a.documentCount;
    }
    return bCreatedAt - aCreatedAt;
  });

  const getDocumentTypeLabel = (type) => {
    const labels = {
      business_bank_statement: 'Bank Statement',
      personal_bank_statement: 'Personal Bank Statement',
      business_tax_return: 'Business Tax Return',
      personal_tax_return: 'Personal Tax Return',
      profit_loss: 'P&L Statement',
      balance_sheet: 'Balance Sheet',
      profit_loss_ytd: 'YTD P&L Statement',
      balance_sheet_ytd: 'YTD Balance Sheet',
      drivers_license: "Driver's License",
      voided_check: 'Voided Check',
      articles_incorporation: 'Articles of Inc.',
      schedule_k1: 'Schedule K-1',
      lease_agreement: 'Lease Agreement',
      mortgage_statement: 'Mortgage Statement',
      operating_agreement: 'Operating Agreement',
    };
    return labels[type] || type;
  };

  const formatDate = (date) => {
     if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Loading admin dashboard...
        </Typography>
      </Container>
    );
  }

  const stats = dashboardData?.overview || {};

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            🔐 Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users, applications, and documents
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), border: 'none' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <PeopleAlt />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), border: 'none' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                  <Assignment />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.usersWithApplication || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Applications
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), border: 'none' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                  <FolderOpen />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalDocuments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documents
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), border: 'none' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.newUsersThisWeek || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New This Week
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search + Sort */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search users by name, email, or business..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: { xs: '100%', md: 500 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 230 } }}>
            <InputLabel>Sort Users</InputLabel>
            <Select
              value={sortMode}
              label="Sort Users"
              onChange={(e) => setSortMode(e.target.value)}
            >
              <MenuItem value="docs_then_recent">Most Docs + Newest</MenuItem>
              <MenuItem value="joined_newest">Joined Date (Newest First)</MenuItem>
              <MenuItem value="joined_oldest">Joined Date (Oldest First)</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Showing {sortedUsers.length} of {usersTotal} users
        </Typography>
      </Paper>

      {/* Users List */}
      <Stack spacing={2}>
        {sortedUsers.map((user) => {
          const isExpanded = expandedUser === user._id;

          return (
            <Card 
              key={user._id} 
              sx={{ 
                border: '1px solid',
                borderColor: user.documentCount > 0 ? 'success.light' : 'grey.200',
                transition: 'all 0.2s',
                '&:hover': { boxShadow: 3 }
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* User Header */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedUser(isExpanded ? null : user._id)}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    {/* Status Chips */}
                    <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                      <Chip
                        size="small"
                        label={user.hasApplication ? 'App ✓' : 'No App'}
                        color={user.hasApplication ? 'success' : 'default'}
                        variant={user.hasApplication ? 'filled' : 'outlined'}
                      />
                      <Chip
                        size="small"
                        label={user.hasPFS ? 'PFS ✓' : 'No PFS'}
                        color={user.hasPFS ? 'success' : 'default'}
                        variant={user.hasPFS ? 'filled' : 'outlined'}
                      />
                      <Badge badgeContent={user.documentCount} color="primary">
                        <Chip
                          size="small"
                          icon={<FolderOpen sx={{ fontSize: 16 }} />}
                          label="Docs"
                          variant="outlined"
                        />
                      </Badge>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                      Joined {formatDate(user.createdAt)}
                    </Typography>

                    {/* Role chip */}
                    <Tooltip title="Change user role">
                      <Chip
                        size="small"
                        icon={<ManageAccountsIcon sx={{ fontSize: '14px !important' }} />}
                        label={user.role || 'user'}
                        color={user.role === 'admin' ? 'error' : user.role === 'sales' ? 'warning' : user.role === 'reviewer' ? 'info' : 'default'}
                        variant="outlined"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ textTransform: 'capitalize', cursor: 'default' }}
                      />
                    </Tooltip>

                    <IconButton size="small">
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </Box>

                {/* Mobile status chips */}
                <Stack direction="row" spacing={1} sx={{ mt: 1, display: { xs: 'flex', sm: 'none' } }}>
                  <Chip size="small" label={user.hasApplication ? 'App ✓' : 'No App'} color={user.hasApplication ? 'success' : 'default'} />
                  <Chip size="small" label={user.hasPFS ? 'PFS ✓' : 'No PFS'} color={user.hasPFS ? 'success' : 'default'} />
                  <Chip size="small" label={`${user.documentCount} Docs`} variant="outlined" />
                </Stack>

                {/* Expanded Content - Documents */}
                <Collapse in={isExpanded}>
                  <Divider sx={{ my: 2 }} />
                  
                  {/* User Details */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">{user.email}</Typography>
                      </Stack>
                    </Grid>
                    {(user.alternateEmails || []).length > 0 && (
                      <Grid item xs={12} sm={4}>
                        <Stack spacing={0.5}>
                          {user.alternateEmails.map((altEmail, idx) => (
                            <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                              <Email sx={{ fontSize: 18, color: 'info.main' }} />
                              <Typography variant="body2" color="info.main">{altEmail}</Typography>
                              <Chip label="alt" size="small" variant="outlined" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                            </Stack>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                    {user.phone && (
                      <Grid item xs={12} sm={4}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.phone}</Typography>
                        </Stack>
                      </Grid>
                    )}
                    {user.businessName && (
                      <Grid item xs={12} sm={4}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.businessName}</Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>

                  {/* Role selector & Delete */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <ManageAccountsIcon sx={{ color: 'text.secondary' }} />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>User Role</InputLabel>
                        <Select
                          value={user.role || 'user'}
                          label="User Role"
                          onChange={(e) => handleRoleChange(user._id, e.target.value)}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="reviewer">Reviewer</MenuItem>
                          <MenuItem value="sales">Sales</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">
                        {user.role === 'sales' && '(Has access to Sales CRM)'}
                        {user.role === 'admin' && '(Full access including Sales CRM)'}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {user.email && (
                        <Tooltip title="Email a link to finish their application and upload missing documents">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={sendingReminder === user._id}
                              startIcon={sendingReminder === user._id ? <CircularProgress size={14} color="inherit" /> : <Email />}
                              onClick={() => handleSendReminder(user)}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', mr: 1 }}
                            >
                              Send Reminder
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Merge this user into another account">
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<MergeType />}
                          onClick={() => { setMergeOpen(user._id); setMergeTarget(''); }}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', mr: 1 }}
                        >
                          Merge
                        </Button>
                      </Tooltip>
                      {deleteConfirm === user._id ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="error">Delete this user?</Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={deletingUser === user._id}
                            onClick={() => handleDeleteUser(user._id)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            {deletingUser === user._id ? <CircularProgress size={14} color="inherit" /> : 'Confirm'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDeleteConfirm(null)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      ) : (
                        <Tooltip title="Delete user and all data">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirm(user._id)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {/* Documents List */}
                  {user.documents && user.documents.length > 0 ? (
                    <Box>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          📁 Uploaded Documents ({user.documents.length})
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={exportingUser === user._id ? <CircularProgress size={14} color="inherit" /> : <Download />}
                          onClick={() => handleExportAllDocuments(user._id, `${user.firstName} ${user.lastName}`)}
                          disabled={exportingUser === user._id}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                        >
                          {exportingUser === user._id ? 'Exporting...' : 'Export All as ZIP'}
                        </Button>
                      </Stack>
                      <List dense sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 1 }}>
                        {user.documents.map((doc) => (
                          <ListItem
                            key={doc._id}
                            sx={{
                              bgcolor: 'white',
                              borderRadius: 1,
                              mb: 0.5,
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                            secondaryAction={
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadDocument(user._id, doc._id, doc.fileName)}
                                  disabled={downloadingDoc === doc._id}
                                >
                                  {downloadingDoc === doc._id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Download sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Description color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {getDocumentTypeLabel(doc.documentType)}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  {doc.fileName} • {formatFileSize(doc.fileSize)} • {formatDate(doc.uploadedAt)}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No documents uploaded yet
                    </Alert>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          );
        })}

        {sortedUsers.length === 0 && (
          <Alert severity="info">
            No users found matching "{searchTerm}"
          </Alert>
        )}

        {users.length < usersTotal && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={loadMoreUsers}
              disabled={loadingMoreUsers}
              startIcon={loadingMoreUsers ? <CircularProgress size={16} /> : null}
            >
              Load more users
            </Button>
          </Box>
        )}
      </Stack>

      {/* Merge Users Dialog */}
      <Dialog open={!!mergeOpen} onClose={() => { setMergeOpen(null); setMergeTarget(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MergeType color="info" />
            <Typography variant="h6">Merge User Into Another Account</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {(() => {
            const sourceUser = users.find(u => u._id === mergeOpen);
            if (!sourceUser) return null;
            return (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  All data (applications, documents, PFS, debt schedules, workbooks, leads) from <strong>{sourceUser.firstName} {sourceUser.lastName}</strong> ({sourceUser.email}) will be transferred to the target account. The email will be saved as an alternate email, then this account will be deleted.
                </Alert>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Source (will be merged &amp; deleted):</Typography>
                <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {sourceUser.firstName} {sourceUser.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{sourceUser.email}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`${sourceUser.documentCount || 0} docs`} variant="outlined" />
                    <Chip size="small" label={sourceUser.hasApplication ? 'Has App' : 'No App'} variant="outlined" />
                  </Stack>
                </Paper>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Target (will keep &amp; receive data):</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select target user</InputLabel>
                  <Select
                    value={mergeTarget}
                    label="Select target user"
                    onChange={(e) => setMergeTarget(e.target.value)}
                  >
                    {users
                      .filter(u => u._id !== mergeOpen)
                      .map(u => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.firstName} {u.lastName} — {u.email}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                {mergeTarget && (() => {
                  const tgt = users.find(u => u._id === mergeTarget);
                  return tgt ? (
                    <Paper sx={{ p: 1.5, mt: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tgt.firstName} {tgt.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{tgt.email}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip size="small" label={`${tgt.documentCount || 0} docs`} variant="outlined" />
                        <Chip size="small" label={tgt.hasApplication ? 'Has App' : 'No App'} variant="outlined" />
                      </Stack>
                    </Paper>
                  ) : null;
                })()}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeOpen(null); setMergeTarget(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!mergeTarget || merging}
            onClick={handleMergeUsers}
            startIcon={merging ? <CircularProgress size={16} color="inherit" /> : <MergeType />}
          >
            {merging ? 'Merging...' : 'Merge Users'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
